// Component: LandingPage.jsx
// Purpose: Public marketing landing page — faithful to the shared design reference
//          (frontend/design-system/ui_kits/marketing/index.html): a single
//          photographic banner carrying the hero, feature glass-cards, and footer.
//          Brand lockup uses the gradient "dt" monogram from the design-system
//          handoff (logo-icon-dark.png), legible on the dark banner — the old
//          black wordmark (logo_small.png) was invisible here.

import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { MdShowChart, MdTimeline, MdSpeed } from "react-icons/md";
import { motion, MotionConfig, useReducedMotion } from "framer-motion";
//* Context: warm gradient "dt + arrow" monogram — design-system primary brand mark.
//  Warm colorway echoes the hero banner's gold up-arrow (the "market moving up" gesture).
import brandMark from "../../data/logo-icon-light.png";
import SignupModal from "../../components/SignupModal";
import LoginOverlay from "../../components/LoginOverlay";
import useThemeColor from "../../hooks/useThemeColor";

// --> Warm brand palette (design-system --brand-* tokens). Replaces the prior blue
//     accents so the hero follows the warm "dt" monogram + rising-line theme.
const BRAND_GRADIENT = "linear-gradient(135deg, #ffb37a 0%, #ff8fb1 50%, #ff5c8e 100%)";
const ACCENT = "#ff8fb1";                                  // eyebrows · link & glyph accents
//  CTA echoes the warm "dt" icon: its peach→pink→coral gradient, embossed (glossy top
//  highlight + soft inner bottom shadow) like the 3D monogram. Warm ink text stays
//  legible on the lighter peach. Outer shadow kept subtle so hover doesn't bloom.
const CTA_BG = BRAND_GRADIENT;                                                          // exact icon gradient
const CTA_BG_HOVER = "linear-gradient(135deg, #ffbf8d 0%, #ffa1c0 50%, #ff749d 100%)";  // a shade brighter
const CTA_EMBOSS = "inset 0 1px 0 rgba(255,255,255,0.60), inset 0 -2px 6px rgba(176,74,96,0.30)";
const CTA_GLOW = `${CTA_EMBOSS}, 0 5px 14px rgba(255,150,160,0.20)`;
const CTA_GLOW_HOVER = `${CTA_EMBOSS}, 0 8px 18px rgba(255,150,160,0.28)`;
const CTA_TEXT = "#5e2236";                                                             // embossed warm ink

// --> Nav links (shared by nav + footer)
const navLinks = [
  { label: "Features", to: "/features" },
  { label: "Pricing", to: "/pricing" },
  { label: "About", to: "/about" },
];

// --> Features data (the 3 real features — now with icons, per the design)
const features = [
  {
    title: "Real-time Market Data",
    description: "Stay updated with live price feeds",
    icon: MdSpeed,
  },
  {
    title: "Advanced Charting",
    description: "Interactive charts with multiple indicators",
    icon: MdShowChart,
  },
  {
    title: "Backtesting",
    description: "Test your strategies against historical data",
    icon: MdTimeline,
  },
];

/*>>> FADE VARIANTS <<<*/
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.0, 0, 0.2, 1] },
  },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const LandingPage = () => {
  /*>>> STATE <<<*/
  //! All state variables preserved from original implementation
  const [scrolled, setScrolled] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showLoginOverlay, setShowLoginOverlay] = useState(false);
  //! On phones the full-width trend-line compresses into an odd diagonal slash —
  //  hide it below 640px and let the breathing glow carry the ambient warmth.
  const [isNarrow, setIsNarrow] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  //* Context: auth state determines CTA text and nav right cluster
  const { isAuthenticated } = useAuth();
  //! Honor OS "reduce motion" — the draw-on line + breathing glow are gated on this.
  const prefersReducedMotion = useReducedMotion();

  // Refs for section elements (preserved from original)
  const heroRef = useRef(null);
  const featuresRef = useRef(null);

  /*>>> EFFECTS <<<*/
  // This page is force-dark (see the data-theme="dark" wrapper below), so pin the
  // phone status bar dark too — otherwise a signed-in user in light mode gets a
  // light bar over the dark landing. Restores to the app theme on unmount.
  useThemeColor("#0d0d0d");

  // Handle any state passed from navigation (preserved exactly)
  useEffect(() => {
    if (location.state?.showLogin) {
      setShowLoginOverlay(true);
    }
  }, [location.state]);

  // Track narrow viewports so the hero trend-line can opt out on phones
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const update = () => setIsNarrow(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  // Scroll listener — drives nav glass transition + parallax scrollY (preserved exactly)
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrollY(currentScrollY);

      const isScrolled = currentScrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    document.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, [scrolled]);

  /*>>> HANDLERS (preserved exactly from original) <<<*/
  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate("/Overview");
    } else {
      setShowSignupModal(true);
      setShowLoginOverlay(false);
    }
  };

  const handleLoginClick = () => {
    setShowLoginOverlay(true);
    setShowSignupModal(false);
  };

  const handleSwitchToSignup = () => {
    setShowLoginOverlay(false);
    setShowSignupModal(true);
  };

  const handleSwitchToLogin = () => {
    setShowSignupModal(false);
    setShowLoginOverlay(true);
  };

  /*>>> STYLES <<<*/
  // Nav — clear at top → frozen-frost glass once scrolled.
  //! Frosted glass, NOT an opaque slab: a translucent *neutral* tint (kills the
  //  blue-band look) + heavy backdrop blur so the warm hero bleeds through, a single
  //  hairline, and no drop-shadow — so it reads as glass, not a band sliding in.
  const navStyle = scrolled
    ? {
        background: "rgba(12, 13, 16, 0.55)",
        backdropFilter: "saturate(140%) blur(18px)",
        WebkitBackdropFilter: "saturate(140%) blur(18px)",
        borderBottom: "1px solid rgba(255,255,255,0.07)",
        boxShadow: "none",
      }
    : {
        background: "transparent",
        backdropFilter: "blur(2px)",
        WebkitBackdropFilter: "blur(2px)",
        borderBottom: "1px solid transparent",
        boxShadow: "none",
      };

  // Single banner: photographic background with parallax (per design ref)
  const bannerStyle = {
    position: "relative",
    overflow: "hidden",
    backgroundImage: `url('/assets/hero-banner.jpg')`,
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    backgroundPosition: `center ${scrollY * 0.5}px`,
  };

  // Dark gradient overlay over the photo — 60 → 50 → 80% (per design ref)
  const overlayStyle = {
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.8) 100%)",
    pointerEvents: "none",
  };

  // Frosted-glass card shared by feature cards + footer (per design ref)
  const glassCard = {
    background: "rgba(255,255,255,0.10)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    border: "1px solid rgba(229,231,235,0.20)",
    borderRadius: "var(--radius-lg)",
  };

  return (
    <MotionConfig reducedMotion="user">
      {/* Fixed dark theme wrapper — does not affect global app theme */}
      <div
        data-theme="dark"
        style={{
          minHeight: "100vh",
          backgroundColor: "#111827",
          color: "#f3f4f6",
          fontFamily: "var(--font-body)",
          position: "relative",
        }}
      >
        {/*>>> SECTION: STICKY GLASS NAV <<<*/}
        <nav
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 50,
            color: "#fff",
            transition: "background 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
            ...navStyle,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px 24px",
              maxWidth: "1152px",
              margin: "0 auto",
            }}
          >
            {/* Brand lockup — gradient monogram + wordmark, white-set for the dark nav */}
            <Link
              to="/"
              aria-label="DeeTrade home"
              style={{ display: "flex", alignItems: "center", gap: "10px", textDecoration: "none" }}
            >
              <img
                src={brandMark}
                alt=""
                aria-hidden="true"
                style={{ height: "32px", width: "32px", display: "block" }}
              />
              <span
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "19px",
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  color: "#fff",
                }}
              >
                DeeTrade
              </span>
            </Link>

            {/* Nav right cluster — text links collapse on mobile (still in footer),
                auth action stays visible. No hamburger on the marketing page. */}
            <div style={{ display: "flex", alignItems: "center", gap: "24px", fontSize: "15px" }}>
              <div className="hidden md:flex" style={{ alignItems: "center", gap: "24px" }}>
                {navLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.to}
                    style={{ color: "#fff", textDecoration: "none", transition: "color 0.2s ease" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = ACCENT)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "#fff")}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>

              {/* Auth-aware action — always visible at every breakpoint */}
              {isAuthenticated ? (
                <Link
                  to="/Overview"
                  style={{
                    background: CTA_BG,
                    color: CTA_TEXT,
                    padding: "8px 18px",
                    borderRadius: "var(--radius-sm)",
                    textDecoration: "none",
                    fontSize: "14px",
                    fontWeight: 600,
                    transition: "background 0.2s ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = CTA_BG_HOVER)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = CTA_BG)}
                >
                  Go to Dashboard
                </Link>
              ) : (
                <button
                  onClick={handleLoginClick}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#fff",
                    fontSize: "15px",
                    fontWeight: 500,
                    padding: 0,
                    cursor: "pointer",
                    transition: "color 0.2s ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = ACCENT)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "#fff")}
                >
                  Log In
                </button>
              )}
            </div>
          </div>
        </nav>

        {/*>>> SECTION: ONE PHOTOGRAPHIC BANNER — hero + features + footer all sit on it <<<*/}
        <section style={bannerStyle}>
          {/* Dark gradient overlay over the photo */}
          <div aria-hidden="true" style={overlayStyle} />

          {/*>>> AMBIENT MOTION — one deliberate moment over the photo, behind the headline.
               A faint warm trend-line draws on once at load (echoing the banner's rising
               arrow), and a soft glow breathes around the arrow so the focal point feels
               alive. Both gated on reduced-motion; pointer-transparent. <<<*/}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "100vh",
              overflow: "hidden",
              pointerEvents: "none",
              zIndex: 1,
            }}
          >
            {/* Breathing warm glow over the arrow (upper-right of the photo) */}
            <motion.div
              style={{
                position: "absolute",
                top: "12%",
                right: "12%",
                width: "min(46vw, 380px)",
                height: "min(46vw, 380px)",
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(255,196,140,0.22) 0%, rgba(255,196,140,0) 65%)",
              }}
              initial={prefersReducedMotion ? false : { opacity: 0.32, scale: 0.96 }}
              animate={
                prefersReducedMotion
                  ? { opacity: 0.38 }
                  : { opacity: [0.3, 0.55, 0.3], scale: [0.96, 1.05, 0.96] }
              }
              transition={
                prefersReducedMotion
                  ? undefined
                  : { duration: 6.5, ease: "easeInOut", repeat: Infinity }
              }
            />

            {/* Faint rising trajectory — drawn on once. Desktop/tablet only; on phones
                the full-width path flattens into an odd diagonal, so we drop it. */}
            {!isNarrow && (
            <svg
              viewBox="0 0 1200 600"
              preserveAspectRatio="none"
              width="100%"
              height="100%"
              style={{ position: "absolute", inset: 0, display: "block" }}
            >
              <defs>
                <linearGradient id="dtTrendStroke" x1="0" y1="1" x2="1" y2="0">
                  <stop offset="0%" stopColor="#ffb37a" stopOpacity="0" />
                  <stop offset="32%" stopColor="#ffb37a" stopOpacity="0.42" />
                  <stop offset="100%" stopColor="#ff8fb1" stopOpacity="0.62" />
                </linearGradient>
                <filter id="dtTrendGlow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="5" result="b" />
                  <feMerge>
                    <feMergeNode in="b" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              <motion.path
                d="M0 478 C 220 462, 360 436, 520 364 C 700 286, 858 250, 1000 152 C 1082 96, 1142 72, 1200 46"
                fill="none"
                stroke="url(#dtTrendStroke)"
                strokeWidth="2"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                filter="url(#dtTrendGlow)"
                initial={prefersReducedMotion ? false : { pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{
                  pathLength: { duration: 2.2, ease: [0.0, 0, 0.2, 1], delay: 0.35 },
                  opacity: { duration: 0.6, delay: 0.35 },
                }}
              />
            </svg>
            )}
          </div>

          {/*>>> HERO <<<*/}
          <motion.div
            ref={heroRef}
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            style={{
              position: "relative",
              zIndex: 10,
              minHeight: "100vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              padding: "96px 24px 0",
            }}
          >
            {/* Eyebrow kicker + headline. Serif italic accent word, warm to match the
                brand monogram + rising line. Typography roles (.ds-eyebrow /
                .ds-display-xl + --font-serif) are the decided ones. */}
            <motion.p
              variants={fadeUp}
              className="ds-eyebrow"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                color: ACCENT,
                margin: "0 0 16px",
                fontSize: "11px",
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: "18px",
                  height: "2px",
                  borderRadius: "2px",
                  background: BRAND_GRADIENT,
                }}
              />
              Algorithmic Trading Platform
            </motion.p>

            <motion.h1
              variants={fadeUp}
              className="ds-display-xl"
              style={{
                fontSize: "clamp(36px, 6vw, 56px)",
                color: "#fff",
                margin: "0 0 16px",
                maxWidth: "16em",
              }}
            >
              Your Gateway to{" "}
              <span
                style={{
                  fontFamily: "var(--font-serif)",
                  fontStyle: "italic",
                  fontWeight: 500,
                  backgroundImage: BRAND_GRADIENT,
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  color: "transparent",
                  filter: "drop-shadow(0 2px 12px rgba(255,140,120,0.28))",
                }}
              >
                Smarter
              </span>{" "}
              Trading
            </motion.h1>

            {/* Sub-copy — softened so the headline carries the contrast */}
            <motion.p
              variants={fadeUp}
              style={{
                fontSize: "18px",
                color: "rgba(255,255,255,0.82)",
                maxWidth: "42rem",
                margin: "0 0 36px",
                lineHeight: 1.55,
              }}
            >
              Experience the future of trading with real-time insights and powerful tools.
            </motion.p>

            {/* Primary CTA (auth-aware) */}
            <motion.div variants={fadeUp}>
              <button
                onClick={handleGetStarted}
                style={{
                  background: CTA_BG,
                  color: CTA_TEXT,
                  padding: "15px 34px",
                  borderRadius: "var(--radius-lg)",
                  fontSize: "16px",
                  fontWeight: 600,
                  letterSpacing: "-0.01em",
                  border: "1px solid rgba(255,255,255,0.22)",
                  cursor: "pointer",
                  boxShadow: CTA_GLOW,
                  transition: "transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = CTA_BG_HOVER;
                  e.currentTarget.style.boxShadow = CTA_GLOW_HOVER;
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = CTA_BG;
                  e.currentTarget.style.boxShadow = CTA_GLOW;
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                {isAuthenticated ? "Go to Dashboard" : "Get Started Free"}
              </button>
            </motion.div>

            {/* Scroll-indicator chevron (bouncing, preserved from original) */}
            <div
              aria-hidden="true"
              className="animate-bounce"
              style={{
                position: "absolute",
                bottom: "40px",
                left: "50%",
                transform: "translateX(-50%)",
                color: "#fff",
                zIndex: 10,
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          </motion.div>

          {/*>>> FEATURES — frosted-glass cards over the same photo <<<*/}
          <motion.div
            ref={featuresRef}
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            style={{
              position: "relative",
              zIndex: 10,
              maxWidth: "1152px",
              margin: "0 auto",
              padding: "64px 24px",
            }}
          >
            <motion.h2
              variants={fadeUp}
              style={{
                fontSize: "30px",
                fontWeight: 600,
                textAlign: "center",
                color: "#fff",
                margin: "0 0 48px",
              }}
            >
              Key Features
            </motion.h2>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: "32px",
              }}
            >
              {features.map((feature) => {
                const IconComp = feature.icon;
                return (
                  <motion.div
                    key={feature.title}
                    variants={fadeUp}
                    style={{
                      ...glassCard,
                      padding: "32px",
                      cursor: "default",
                      transition: "background 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease",
                    }}
                    whileHover={{
                      y: -6,
                      background: "rgba(255,255,255,0.20)",
                      boxShadow: "0 25px 50px -12px rgba(0,0,0,0.45)",
                      transition: { duration: 0.2, ease: [0.0, 0, 0.2, 1] },
                    }}
                  >
                    {/* Accent-tinted icon chip */}
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "44px",
                        height: "44px",
                        borderRadius: "var(--radius)",
                        background: "rgba(255,143,177,0.16)",
                        marginBottom: "16px",
                      }}
                    >
                      <IconComp size={22} style={{ color: ACCENT }} />
                    </div>

                    <h3
                      style={{
                        fontSize: "20px",
                        fontWeight: 600,
                        color: "#fff",
                        margin: "0 0 12px",
                      }}
                    >
                      {feature.title}
                    </h3>
                    <p
                      style={{
                        fontSize: "15px",
                        color: "#e5e7eb",
                        lineHeight: 1.5,
                        margin: 0,
                      }}
                    >
                      {feature.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/*>>> FOOTER — quiet hairline strip on the same photo (no card slab) <<<*/}
          <footer
            style={{
              position: "relative",
              zIndex: 10,
              padding: "0 24px 36px",
              marginTop: "40px",
            }}
          >
            <div
              style={{
                maxWidth: "1152px",
                margin: "0 auto",
                borderTop: "1px solid rgba(255,255,255,0.10)",
                paddingTop: "22px",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "16px",
              }}
            >
              {/* Brand lockup + attribution */}
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <img
                  src={brandMark}
                  alt=""
                  aria-hidden="true"
                  style={{ height: "22px", width: "22px", display: "block" }}
                />
                <span
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: "15px",
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                    color: "#fff",
                  }}
                >
                  DeeTrade
                </span>
                <span style={{ color: "rgba(255,255,255,0.25)" }}>·</span>
                <span style={{ fontSize: "12px", color: "#9ca3af", fontStyle: "italic", letterSpacing: "0.02em" }}>
                  By DEESUN
                </span>
              </div>

              {/* Nav links + copyright — single low row, wraps on mobile */}
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "24px" }}>
                {navLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.to}
                    style={{ fontSize: "13px", color: "#d1d5db", textDecoration: "none", transition: "color 0.2s ease" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = ACCENT)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "#d1d5db")}
                  >
                    {link.label}
                  </Link>
                ))}
                <span style={{ fontSize: "13px", color: "#9ca3af", whiteSpace: "nowrap" }}>
                  © 2026 DeeTrade. All rights reserved.
                </span>
              </div>
            </div>
          </footer>
        </section>

        {/* Pass onSwitch to modals so they can signal to switch between login and signup
            Props preserved exactly from original implementation */}
        <SignupModal
          isOpen={showSignupModal}
          onClose={() => setShowSignupModal(false)}
          onSwitchToLogin={handleSwitchToLogin}
        />

        <LoginOverlay
          isOpen={showLoginOverlay}
          onClose={() => {
            setShowLoginOverlay(false);
            // Clear the navigation state
            window.history.replaceState({}, document.title);
          }}
          onSwitchToSignup={handleSwitchToSignup}
        />
      </div>
    </MotionConfig>
  );
};

export default LandingPage;
